// @ts-strict-ignore
import {Injectable} from "@angular/core";
import {
	AssessableItem,
	AssessableItemGroup,
	Assessment,
	AssessmentItem,
	AssessmentState,
	ChecklistItemType,
	UserRecordingAssessment,
} from "@models";

@Injectable({
	providedIn: "root",
})
export class SimAssessmentScoreService {
	public getAssessmentScore(assessment: Assessment): number {
		return assessment.assessableGroups.reduce((total, g) => total + this.getGroupScore(g, assessment), 0);
	}

	public getAssessmentMaxScore(assessment: Assessment): number {
		return assessment.assessableGroups.reduce((total, g) => total + this.getGroupMaxScore(g), 0);
	}

	public getGroupScore(group: AssessableItemGroup<AssessmentItem>, assessment: Assessment): number {
		if (!this._isActivated(group, assessment)) {
			return 0;
		}
		return group.items.reduce((total, i) => total + this.getItemScore(i), 0);
	}

	public getItemScore(item: AssessmentItem): number {
		switch (item.type) {
			case ChecklistItemType.YesNoOption: {
				let index = -1;
				if (typeof item.answer === "boolean") {
					index = item.answer ? 1 : 0;
				}
				return item.yesNoScores[index] ?? 0;
			}

			case ChecklistItemType.SingleChoiceOption:
				return item.selectableOptions[item.answer]?.score ?? 0;

			case ChecklistItemType.MultipleChoiceOption: {
				if (Array.isArray(item.answer)) {
					const total = item.selectableOptions.reduce(
						(total, option, index) => total + (item.answer.includes(index) ? option.score : 0),
						0,
					);

					return item.isScoreBoundedByZero ? Math.max(0, total) : total;
				}
				return 0;
			}

			case ChecklistItemType.FreeAnswerOption:
				return Math.max(0, item.freeTextScore ?? 0);

			case ChecklistItemType.ThreeStarOption: {
				return item.starScores[item.answer] ?? 0;
			}
		}
	}

	public getGroupMaxScore(group: AssessableItemGroup<AssessmentItem>) {
		return group.items.reduce((total, i) => total + this.getItemMaxScore(i), 0);
	}

	public getItemMaxScore(item: AssessmentItem) {
		switch (item.type) {
			case ChecklistItemType.YesNoOption:
				return Math.max(item.yesNoScores[0], item.yesNoScores[1]);

			case ChecklistItemType.MultipleChoiceOption:
				return item.selectableOptions.reduce((total, option) => total + Math.max(option.score, 0), 0);

			case ChecklistItemType.FreeAnswerOption:
				return item.freeTextMaxScore;

			case ChecklistItemType.ThreeStarOption:
				return Math.max(...item.starScores);

			case ChecklistItemType.SingleChoiceOption:
				return item.selectableOptions.reduce((current, option) => Math.max(current, option.score), 0);
		}
	}

	private _isActivated<T extends AssessableItem>(group: AssessableItemGroup<T>, assessment: Assessment): boolean {
		const activationCondition = group.activationCondition;
		if (!activationCondition) {
			return true;
		}

		const items = assessment.assessableGroups.flatMap((g) => g.items);

		const activatedByItem = items.find((i) => i.assessableItemId === activationCondition.activationItemId);
		if (!activatedByItem) {
			return true;
		}

		switch (activatedByItem.type) {
			case ChecklistItemType.ThreeStarOption:
				return activatedByItem.answer >= activationCondition.activationItemAnswer;

			case ChecklistItemType.MultipleChoiceOption:
				const answer = activatedByItem.answer as number[];
				const activationItemAnswer = activationCondition.activationItemAnswer as number[];
				return (
					answer != null &&
					activationItemAnswer != null &&
					answer.length === activationItemAnswer.length &&
					answer.every((a) => activationItemAnswer.includes(a))
				);

			case ChecklistItemType.FreeAnswerOption:
				return activatedByItem.answer === activationCondition.activationItemAnswer;
			case ChecklistItemType.SingleChoiceOption:
				return activatedByItem.answer === activationCondition.activationItemAnswer;
			case ChecklistItemType.YesNoOption:
				return activatedByItem.answer === activationCondition.activationItemAnswer;
		}
	}

	public static getAssessmentGrade(userAssessment: UserRecordingAssessment): IAssessmentGrade {
		if (!userAssessment) return null;

		if (userAssessment.state === AssessmentState.Passed || userAssessment.state === AssessmentState.Failed) {
			return {
				state: userAssessment.state,
				isFinal: true,
			};
		}

		if (userAssessment.state === AssessmentState.Aborted) {
			return {
				state: AssessmentState.Failed,
				isFinal: true,
			};
		}

		return {
			state: userAssessment.assessmentStateTemporary,
			isFinal: false,
		};
	}

	public static getTotalGrade(userAssessments: UserRecordingAssessment[]): IAssessmentGrade {
		if (!userAssessments || userAssessments.length === 0) return null;
		const validAssessmentGrades = userAssessments.map((x) => this.getAssessmentGrade(x)).filter((x) => x !== null);
		const passedAssessmentGrades = validAssessmentGrades.filter((x) => x.state == AssessmentState.Passed);
		const totalGrade =
			passedAssessmentGrades.length > Math.floor(validAssessmentGrades.length / 2) ? AssessmentState.Passed : AssessmentState.Failed;
		const isFinal = validAssessmentGrades.every((x) => x.isFinal);
		return {
			state: totalGrade,
			isFinal: isFinal,
		};
	}

	public static getScorePercentage(userAssessment: UserRecordingAssessment) {
		if (!userAssessment) return null;
		return (userAssessment.reachedScore || userAssessment.reachedScoreTemporary) / userAssessment.maxScore;
	}
}

export interface IAssessmentGrade {
	state: AssessmentState;
	isFinal: boolean;
}
